
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar, RefreshCw, Check, FileCheck, FileText, Send, Download, CheckCheck, Users, User, Clock, CheckCircle, Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { apiService } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { useAppDataContext } from "@/contexts/AppDataContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Employee, Payslip } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import {
  canApprovePayrollStep,
  canProcessPayroll,
  getRoleLabel,
  isPayrollApprovalOnlyRole,
  normalizeUserRole,
} from "@/lib/roles";
import {
  matchPayslipToPrevious,
  payslipAllowanceBreakdown,
  payslipBasicSalary,
  payslipDeductionBreakdown,
  payslipEmployerNapsa,
  payslipEmployerNhima,
  payslipLineEmployerAmount,
  payslipNetSalary,
  payslipStatutoryAmount,
  payslipWageBill,
  resolvePayslipEmployee,
} from "@/lib/payslipEmployee";
import { formatCurrency } from "@/lib/utils";
import { PayslipService } from "@/services/payslipService";
import { AllowancePayeBadges } from "@/components/payroll/AllowancePayeBadges";

export interface PayrollProcessorProps {
  onProcessed?: () => void;
}

export const PayrollProcessor = ({ onProcessed }: PayrollProcessorProps) => {
  const [processingPayroll, setProcessingPayroll] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().substring(0, 7)); // YYYY-MM format
  const { toast } = useToast();
  const { refresh, data } = useAppDataContext();
  const { user } = useAuth();
  const { employees } = data;
  const exportCouncilId =
    user?.cid != null && Number(user.cid) > 0 ? Number(user.cid) : null;
  
  // State for payroll mode
  const [processingMode, setProcessingMode] = useState<"individual" | "batch">("batch");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [selectedEmployees, setSelectedEmployees] = useState<Record<string, boolean>>({});
  
  // Initialize selected employees
  useEffect(() => {
    const initialSelection = employees.reduce((acc, emp) => {
      acc[emp.id] = true;
      return acc;
    }, {} as Record<string, boolean>);
    
    setSelectedEmployees(initialSelection);
  }, [employees]);
  
  const userRole = user ? normalizeUserRole(user.role) : 'employee';
  const approvalOnlyPayroll = isPayrollApprovalOnlyRole(userRole);
  const canRunPayroll = canProcessPayroll(userRole);

  // State for approval workflow
  const [activeTab, setActiveTab] = useState("process");

  useEffect(() => {
    if (approvalOnlyPayroll) {
      setActiveTab("approval");
    }
  }, [approvalOnlyPayroll]);
  const [payrollData, setPayrollData] = useState({
    grossPay: 0,
    taxableIncome: 0,
    totalDeductions: 0,
    netPay: 0,
    employeeCount: 0,
    status: "draft",
    processedPayslips: [] as Payslip[]
  });
  
  // State for exports
  const [generatingExport, setGeneratingExport] = useState(false);
  const [exportType, setExportType] = useState("");
  const [exportPreview, setExportPreview] = useState<any>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [uniqueDeductionNames, setUniqueDeductionNames] = useState<any[]>([]);
  
  // State for reports
  const [reportPayslips, setReportPayslips] = useState<Payslip[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  
  // Add state for selected month and payslips for that month (for approval tab)
  const [approvalMonth, setApprovalMonth] = useState(() => new Date().toISOString().substring(0, 7));
  const [approvalPayslips, setApprovalPayslips] = useState<Payslip[]>([]);
  const [fetchingApprovalPayslips, setFetchingApprovalPayslips] = useState(false);

  // Fetch payslips for the selected approval month
  useEffect(() => {
    const fetchPayslips = async () => {
      setFetchingApprovalPayslips(true);
      try {
        const payslips = await PayslipService.getPayslipsByPeriod(approvalMonth, user?.cid ?? null);
        setApprovalPayslips(payslips.filter((p) => p.period === approvalMonth));
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to fetch payslips for the selected month.', variant: 'destructive' });
      } finally {
        setFetchingApprovalPayslips(false);
      }
    };
    fetchPayslips();
  }, [approvalMonth, user?.cid]);
  
  const [approvalStatus, setApprovalStatus] = useState<any>(null);
 
  const [fetchingApprovalStatus, setFetchingApprovalStatus] = useState(false);
  const [approving, setApproving] = useState(false);

  // Fetch approval status for approvalMonth
  useEffect(() => {
    const fetchStatus = async () => {
      setFetchingApprovalStatus(true);
      try {
        const res = await apiService.getPayrollApprovalStatus(approvalMonth, user?.cid ?? null);
        setApprovalStatus(res.data || res);
        setPayrollData({
          ...payrollData,
          status: res.data.status,
        });
      } catch (e) {
        setApprovalStatus(null);
      } finally {
        setFetchingApprovalStatus(false);
      }
    };
    fetchStatus();
  }, [approvalMonth, fetchingApprovalPayslips, user?.cid]);

  // Load unique deduction names for dynamic export options (council-scoped)
  useEffect(() => {
    const loadUniqueDeductionNames = async () => {
      try {
        if (exportCouncilId == null) {
          setUniqueDeductionNames([]);
          return;
        }
        const res = await apiService.getUniqueDeductionNames(exportCouncilId);
        setUniqueDeductionNames(res.data || res || []);
      } catch (error) {
        console.error('Error loading unique deduction names:', error);
      }
    };
    loadUniqueDeductionNames();
  }, [exportCouncilId]);

  const handleApproveStep = async () => {
    if (!user?.id) return;
    setApproving(true);
    try {
      await apiService.approvePayroll({
        period: approvalMonth,
        role: userRole,
        user_id: Number(user.id),
        cid: user?.cid ?? undefined,
      });
      // Refetch approval status after approval
      const res = await apiService.getPayrollApprovalStatus(approvalMonth, user?.cid ?? null);
      setApprovalStatus(res.data || res);

      setPayrollData({
        ...payrollData,
        status: res.data.status,
      });
     
      toast({ title: 'Success', description: 'Approval updated.' });
      try {
        await apiService.addAuditLog({
          action: 'payroll_approval_step',
          module: 'payroll',
          description: `Payroll ${approvalMonth} approved as ${userRole}`,
          user_id: Number(user.id),
          role: userRole,
          period: approvalMonth,
          cid: user?.cid ?? null,
        });
      } catch {
        // Non-blocking audit log write.
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to approve step.', variant: 'destructive' });
    } finally {
      setApproving(false);
    }
  };
  
  const handleProcessPayroll = async () => {
    setProcessingPayroll(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      let employeesToProcess: Employee[] = [];
      if (processingMode === "individual") {
        const selectedEmployee = employees.find(emp => emp.id === selectedEmployeeId);
        if (!selectedEmployee) {
          throw new Error("Selected employee not found");
        }
        employeesToProcess = [selectedEmployee];
      } else {
        employeesToProcess = employees.filter(emp => selectedEmployees[emp.id]);
      }
      if (employeesToProcess.length === 0) {
        throw new Error("No employees selected for processing");
      }
      // Call the API to process payroll and get the real data
      const councilId = user?.cid != null ? Number(user.cid) : null;
      const result = await apiService.processPayroll(
        processingMode === "individual"
          ? { employee_id: selectedEmployeeId, period: selectedMonth, ...(councilId ? { cid: councilId } : {}) }
          : {
              period: selectedMonth,
              ...(councilId ? { cid: councilId } : {}),
            }
      );
      // For batch, result is an array; for individual, it's a single object
      let processedPayslips = [];
      if (Array.isArray(result.data)) {
        processedPayslips = result.data;
      } else if (result.data) {
        processedPayslips = [result.data];
      }
      const skipped = processedPayslips.filter((row: { error?: string }) => row?.error);
      const successful = processedPayslips.filter((row: { error?: string }) => !row?.error);
      if (skipped.length > 0) {
        toast({
          title: "Some employees were skipped",
          description:
            `${skipped.length} employee(s) already have payslips for this period. Use "Rerun entire payroll" after changing allowance PAYE settings so figures are recalculated.`,
          variant: "destructive",
        });
      }
      if (successful.length === 0) {
        throw new Error(
          skipped.length > 0
            ? "No new payslips were created because this period was already processed. Use Rerun entire payroll to refresh figures."
            : "No employees were processed."
        );
      }
      // Calculate summary from real data
      const employeeCount = successful.length;
      const grossPay = successful.reduce((sum, p) => sum + (p.basic_salary || 0) + (p.total_allowances || 0), 0);
      const netPay = successful.reduce((sum, p) => sum + (p.net_salary || 0), 0);
      const totalDeductions = successful.reduce((sum, p) => sum + (p.total_deductions || 0), 0);
      setPayrollData({
        grossPay,
        taxableIncome: grossPay, // or adjust as needed
        totalDeductions,
        netPay,
        employeeCount,
        status: "pending_hr",
        processedPayslips
      });
      toast({
        title: "Payroll processed",
        description: `Payroll for ${formatPayPeriod(selectedMonth)} has been processed for ${employeeCount} employee(s) and is pending approval.${
          skipped.length > 0 ? ` ${skipped.length} employee(s) were skipped because payslips already exist.` : ""
        }`,
      });
      try {
        await apiService.addAuditLog({
          action: 'payroll_processed',
          module: 'payroll',
          description: `Processed payroll for ${selectedMonth} (${employeeCount} employees)`,
          user_id: Number(user?.id ?? 0),
          role: userRole,
          period: selectedMonth,
          cid: user?.cid ?? null,
        });
      } catch {
        // Non-blocking audit log write.
      }

      setActiveTab("approval");
      if (onProcessed) {
        onProcessed();
      }
    } catch (error) {
      console.error('Error processing payroll:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to process payroll. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingPayroll(false);
    }
  };
  
  const formatPayPeriod = (period: string) => {
    const date = new Date(period + "-01");
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const formatMoney = (value: number) => formatCurrency(Number(value || 0));

  // Sign-off order: 1 HR → 2 Finance → 3 Audit → 4 Secretary
  const approvalSignOffLabels = {
    hr: approvalStatus?.hr_approved_by_name ? `${approvalStatus.hr_approved_by_name} at ${approvalStatus.hr_approved_at}` : 'Pending',
    finance: approvalStatus?.finance_approved_by_name ? `${approvalStatus.finance_approved_by_name} at ${approvalStatus.finance_approved_at}` : 'Pending',
    audit: approvalStatus?.audit_approved_by_name ? `${approvalStatus.audit_approved_by_name} at ${approvalStatus.audit_approved_at}` : 'Pending',
    secretary: approvalStatus?.secretary_approved_by_name ? `${approvalStatus.secretary_approved_by_name} at ${approvalStatus.secretary_approved_at}` : 'Pending',
  };
  


  // const handleApprove = async (role: string) => {
  //   try {
  //     // Simulate API call
  //     await new Promise(resolve => setTimeout(resolve, 1000));
      
  //     let newStatus = "pending_hr";
  //     let message = "";
      
  //     switch (role) {
  //       case "hr":
  //         newStatus = "pending_finance";
  //         message = "HR Officer has verified the payroll data";
  //         break;
  //       case "finance":
  //         newStatus = "pending_audit";
  //         message = "Finance Officer has approved the figures";
  //         break;
  //       case "audit":
  //         newStatus = "pending_final";
  //         message = "Internal Auditor has confirmed compliance";
  //         break;
  //       case "secretary":
  //         newStatus = "approved";
  //         message = "Final approval granted by Secretary/Town Clerk";
          
  //         // Update all payslips to approved
  //         if (payrollData.processedPayslips.length > 0) {
  //           for (const payslip of payrollData.processedPayslips) {
  //             await apiService.updatePayslip(payslip.id, { ...payslip, status: 'approved' });
  //           }
            
  //           // Refresh payslips data
  //           await refresh.payslips();
  //         }
  //         break;
  //     }
      
  //     setPayrollData({
  //       ...payrollData,
  //       status: newStatus,
  //     });
      
  //     toast({
  //       title: "Approval updated",
  //       description: message,
  //     });
      
  //   } catch (error) {
  //     console.error('Error updating approval:', error);
  //     toast({
  //       title: "Error",
  //       description: "Failed to update approval status.",
  //       variant: "destructive",
  //     });
  //   }
  // };
  
  const requireExportCouncil = (): number | null => {
    if (exportCouncilId == null || exportCouncilId <= 0) {
      toast({
        title: "Council required",
        description:
          "Your account must be linked to a council to export deduction reports.",
        variant: "destructive",
      });
      return null;
    }
    return exportCouncilId;
  };

  const BUILTIN_REPORT_TYPES = new Set([
    "Monthly Payroll Summary",
    "Employee Earnings Statement",
    "Employee Deductions Statement",
    "PAYE & Statutory Contributions Report",
    "NAPSA Return",
    "ZRA PAYE Return",
    "Payflex Report",
    "Variance Report (Current vs. Previous)",
    "Staff Allowances Report",
    "NHIMA Contributions Report",
    "Funeral Contributions Report",
  ]);

  const isMoneyPreviewHeader = (header: string) => {
    if (/%/.test(header)) return false;
    return /salary|pay|amount|allowance|contribution|deduction|paye|napsa|nhima|gross|net|total|variance|tax|remit|wage/i.test(
      header
    );
  };

  const formatPreviewCell = (value: unknown, header?: string): string => {
    if (typeof value === "number") {
      return header && isMoneyPreviewHeader(header) ? formatCurrency(value) : value.toFixed(2);
    }
    if (
      typeof value === "string" &&
      header &&
      isMoneyPreviewHeader(header) &&
      /^-?\d+(\.\d+)?$/.test(value.trim())
    ) {
      return formatCurrency(Number(value));
    }
    return value == null ? "" : String(value);
  };

  const getExportPreviewFileBase = () => {
    const slug = exportType
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
    return `${slug || "report"}_${selectedMonth}`;
  };

  /** Keep only rows for the signed-in user's council (defense in depth). */
  const filterDeductionRowsByCouncil = (rows: any[], cid: number) => {
    if (!Array.isArray(rows)) return [];
    return rows.filter(
      (item) => Number(item?.employee_cid ?? item?.cid) === cid
    );
  };

  const fetchDeductionExportData = async (deductionName: string, cid: number) => {
    const response = await apiService.getPayslipDeductionsByPeriodAndName({
      period: selectedMonth,
      name: deductionName,
      cid,
    });
    const raw = response?.data ?? response ?? [];
    return filterDeductionRowsByCouncil(Array.isArray(raw) ? raw : [], cid);
  };

  const generatePreview = async () => {
    try {
      if (!exportType) {
        toast({
          title: "Error",
          description: "Please select a deduction type first.",
          variant: "destructive",
        });
        return;
      }

      const cid = requireExportCouncil();
      if (cid == null) return;

      if (BUILTIN_REPORT_TYPES.has(exportType)) {
        toast({
          title: "Use Available Reports",
          description:
            "For payroll reports, use the buttons under Available Reports (not the deduction type dropdown).",
        });
        return;
      }

      const deductionsData = await fetchDeductionExportData(exportType, cid);
      
      if (deductionsData.length === 0) {
        setExportPreview([]);
        toast({
          title: "No Data",
          description: `No ${exportType} deductions found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }

      // Generate preview based on the actual data from database
      const previewData = generateDeductionPreviewFromData(deductionsData, exportType);
      setExportPreview(previewData);
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast({
        title: "Error",
        description: "Failed to generate preview.",
        variant: "destructive",
      });
    }
  };

  const handleGenerateExport = async () => {
    setGeneratingExport(true);
    
    try {
      if (!exportType) {
        toast({
          title: "Error",
          description: "Please select a report type first.",
          variant: "destructive",
        });
        return;
      }

      let csvData: any[] = [];
      let fileName = '';

      // Handle different report types
      if (exportType === "Monthly Payroll Summary") {
        if (exportPreview?.summary && exportPreview?.details) {
          downloadMonthlyPayrollSummaryCsv(
            exportPreview.summary,
            exportPreview.details,
            `monthly_payroll_summary_${selectedMonth}.csv`
          );
          toast({
            title: "Export complete",
            description: `monthly_payroll_summary_${selectedMonth}.csv has been generated and downloaded successfully.`,
          });
          try {
            await apiService.addAuditLog({
              action: 'payroll_export',
              module: 'payroll',
              description: `Generated export monthly_payroll_summary_${selectedMonth}.csv`,
              user_id: Number(user?.id ?? 0),
              role: userRole,
              period: selectedMonth,
              cid: user?.cid ?? null,
            });
          } catch {
            // Non-blocking audit log write.
          }
          return;
        }
        csvData = Array.isArray(exportPreview) ? exportPreview : [];
        fileName = `monthly_payroll_summary_${selectedMonth}.csv`;
      } else if (exportType === "Employee Earnings Statement") {
        csvData = Array.isArray(exportPreview) ? exportPreview : [];
        fileName = `employee_earnings_statement_${selectedMonth}.csv`;
      } else if (exportType === "Employee Deductions Statement") {
        csvData = Array.isArray(exportPreview) ? exportPreview : [];
        fileName = `employee_deductions_statement_${selectedMonth}.csv`;
      } else if (exportType === "PAYE & Statutory Contributions Report") {
        csvData = Array.isArray(exportPreview) ? exportPreview : [];
        fileName = `paye_statutory_contributions_${selectedMonth}.csv`;
      } else if (exportType === "NAPSA Return") {
        csvData = Array.isArray(exportPreview) ? exportPreview : [];
        fileName = `napsa_return_${selectedMonth}.csv`;
      } else if (exportType === "ZRA PAYE Return") {
        csvData = Array.isArray(exportPreview) ? exportPreview : [];
        fileName = `zra_paye_return_${selectedMonth}.csv`;
      } else if (exportType === "Variance Report (Current vs. Previous)") {
        csvData = Array.isArray(exportPreview) ? exportPreview : [];
        fileName = `variance_report_${selectedMonth}.csv`;
      } else if (exportType === "Payflex Report") {
        csvData = Array.isArray(exportPreview) ? exportPreview : [];
        fileName = `payflex_report_${selectedMonth}.csv`;
      } else if (exportType === "Staff Allowances Report") {
        csvData = Array.isArray(exportPreview) ? exportPreview : [];
        fileName = `staff_allowances_report_${selectedMonth}.csv`;
      } else if (exportType === "NHIMA Contributions Report") {
        csvData = Array.isArray(exportPreview) ? exportPreview : [];
        fileName = `nhima_contributions_report_${selectedMonth}.csv`;
      } else if (exportType === "Funeral Contributions Report") {
        csvData = Array.isArray(exportPreview) ? exportPreview : [];
        fileName = `funeral_contributions_report_${selectedMonth}.csv`;
      } else {
        // Legacy deduction export handling
        const cid = requireExportCouncil();
        if (cid == null) {
          setGeneratingExport(false);
          return;
        }
        const deductionsData = await fetchDeductionExportData(exportType, cid);
        
        if (deductionsData.length === 0) {
          toast({
            title: "No Data",
            description: `No ${exportType} deductions found for ${formatPayPeriod(selectedMonth)}.`,
            variant: "destructive",
          });
          return;
        }

        // Generate export data from actual database data
        const exportData = generateDeductionPreviewFromData(deductionsData, exportType);
        fileName = `${exportType.toLowerCase().replace(/\s+/g, '_')}_${selectedMonth}.csv`;

        // Handle different data structures for download
        if (Array.isArray(exportData)) {
          csvData = exportData;
        } else if (exportData && exportData.transactions) {
          if (exportData.summary) {
            downloadNapsaReportCsv(
              exportData.summary,
              exportData.transactions,
              fileName
            );
            toast({
              title: "Export complete",
              description: `${fileName} has been generated and downloaded successfully.`,
            });
            try {
              await apiService.addAuditLog({
                action: 'payroll_export',
                module: 'payroll',
                description: `Generated export ${fileName}`,
                user_id: Number(user?.id ?? 0),
                role: userRole,
                period: selectedMonth,
                cid: user?.cid ?? null,
              });
            } catch {
              // Non-blocking audit log write.
            }
            return;
          }
          csvData = exportData.transactions;
        }
      }

      if (exportType.toLowerCase().includes('napsa') && exportPreview?.summary && exportPreview?.transactions) {
        downloadNapsaReportCsv(
          exportPreview.summary,
          exportPreview.transactions,
          fileName || `napsa_return_${selectedMonth}.csv`
        );
        toast({
          title: "Export complete",
          description: `${fileName || `napsa_return_${selectedMonth}.csv`} has been generated and downloaded successfully.`,
        });
        try {
          await apiService.addAuditLog({
            action: 'payroll_export',
            module: 'payroll',
            description: `Generated export ${fileName || `napsa_return_${selectedMonth}.csv`}`,
            user_id: Number(user?.id ?? 0),
            role: userRole,
            period: selectedMonth,
            cid: user?.cid ?? null,
          });
        } catch {
          // Non-blocking audit log write.
        }
        return;
      }

      if (csvData.length === 0) {
        toast({
          title: "No Data",
          description: "No data available to export.",
          variant: "destructive",
        });
        return;
      }

      // Download the file
      downloadCSV(csvData, fileName);
      
      toast({
        title: "Export complete",
        description: `${fileName} has been generated and downloaded successfully.`,
      });
      try {
        await apiService.addAuditLog({
          action: 'payroll_export',
          module: 'payroll',
          description: `Generated export ${fileName}`,
          user_id: Number(user?.id ?? 0),
          role: userRole,
          period: selectedMonth,
          cid: user?.cid ?? null,
        });
      } catch {
        // Non-blocking audit log write.
      }
      
    } catch (error) {
      console.error('Error generating export:', error);
      toast({
        title: "Error",
        description: "Failed to generate the requested export.",
        variant: "destructive",
      });
    } finally {
      setGeneratingExport(false);
    }
  };

  // Helper function to download CSV
  const escapeCsvValue = (value: unknown): string => {
    const str = value == null ? '' : String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const downloadCsvFile = (content: string, fileName: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadMonthlyPayrollSummaryCsv = (
    summary: Record<string, string>,
    details: Record<string, string>[],
    fileName: string
  ) => {
    const lines: string[] = ['Payroll Summary', 'Metric,Value'];
    Object.entries(summary).forEach(([key, value]) => {
      lines.push(`${escapeCsvValue(key)},${escapeCsvValue(value)}`);
    });
    lines.push('');
    lines.push('Employee Details');
    if (details.length > 0) {
      const headers = Object.keys(details[0]);
      lines.push(headers.map(escapeCsvValue).join(','));
      details.forEach((row) => {
        lines.push(headers.map((h) => escapeCsvValue(row[h])).join(','));
      });
    }
    downloadCsvFile(lines.join('\n'), fileName);
  };

  const downloadNapsaReportCsv = (
    summary: Record<string, string>,
    transactions: Record<string, string>[],
    fileName: string
  ) => {
    const lines: string[] = ['NAPSA Retirement Summary', 'Metric,Value'];
    Object.entries(summary).forEach(([key, value]) => {
      lines.push(`${escapeCsvValue(key)},${escapeCsvValue(value)}`);
    });
    lines.push('');
    lines.push('Individual Employee Contributions');
    if (transactions.length > 0) {
      const headers = Object.keys(transactions[0]);
      lines.push(headers.map(escapeCsvValue).join(','));
      transactions.forEach((row) => {
        lines.push(headers.map((h) => escapeCsvValue(row[h])).join(','));
      });
    }
    downloadCsvFile(lines.join('\n'), fileName);
  };

  const manNumberFromDeductionItem = (item: Record<string, unknown>): string => {
    if (item.manNumber || item.man_number) {
      return String(item.manNumber || item.man_number);
    }
    const internalId = item.employee_id ?? item.employeeId;
    const emp = employees.find(
      (e) =>
        String(e.id) === String(internalId) ||
        String(e.employeeId) === String(item.employeeId)
    );
    return emp?.manNumber || 'N/A';
  };

  const downloadCSV = (data: any[], fileName: string) => {
    if (data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in CSV
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Generate Bank Schedule
  const generateBankSchedule = (payslips: Payslip[]) => {
    return payslips.map((payslip) => {
      const emp = resolvePayslipEmployee(payslip, employees);
      return {
        'Man Number': emp.manNumber,
        'Employee Name': emp.name,
        'Bank Name': emp.bankName,
        'Account Number': emp.bankAccount,
        'Branch': emp.bankBranch,
        'Net Salary': payslipNetSalary(payslip),
        'Period': payslip.period,
      };
    });
  };

  /** Payflex bank payment file — sort code + branch per employee (item #12 in backlog). */
  const generatePayflexReport = (payslips: Payslip[]) => {
    return payslips.map((payslip) => {
      const emp = resolvePayslipEmployee(payslip, employees);
      return {
        'Man Number': emp.manNumber,
        'Employee Name': emp.name,
        'Bank Name': emp.bankName,
        'Account Number': emp.bankAccount,
        'Sort Code': emp.swiftcode,
        'Branch': emp.bankBranch,
        'Net Pay': payslipNetSalary(payslip),
        Period: payslip.period || selectedMonth,
      };
    });
  };

  // Generate NAPSA Schedule
  const generateNapsaSchedule = (payslips: Payslip[]) => {
    // Sum NAPSA from deductionBreakdown
    const totalContribution = payslips.reduce((sum, payslip) => {
      const napsa = (payslip.deductionBreakdown || [])
        .filter((d: any) => d.name && d.name.toLowerCase().includes('napsa'))
        .reduce((s: number, d: any) => s + Number(d.amount || 0), 0);
      return sum + napsa;
    }, 0);

    const employeeNapsa = totalContribution;
    const employerNapsa = totalContribution;

    return [{
      'Period': selectedMonth,
      'Total Employees': payslips.length,
      'Total Basic Pay': payslips.reduce((sum, p) => sum + (Number(p.basicSalary) || 0), 0),
      'NAPSA Rate (Employee)': '5%',
      'NAPSA Rate (Employer)': '5%',
      'Employee NAPSA (5%)': employeeNapsa,
      'Employer NAPSA (5%)': employerNapsa,
      'Total NAPSA to Remit (10%)': employeeNapsa + employerNapsa,
    }];
  };

  // Generate NHIMA schedule — one detail row per employee (employer = employee 1%)
  const generateNhimaSchedule = (payslips: Payslip[]) => {
    const rows = payslips
      .map((payslip) => {
        const emp = resolvePayslipEmployee(payslip, employees);
        const employeeContribution = payslipStatutoryAmount(payslip, "nhima");
        const employerContribution = payslipEmployerNhima(payslip);
        return {
          "Employee Name": emp.name,
          "Man Number": emp.manNumber || "",
          "Employee Contribution": employeeContribution.toFixed(2),
          "Employer Contribution": employerContribution.toFixed(2),
          Total: (employeeContribution + employerContribution).toFixed(2),
        };
      })
      .filter(
        (row) =>
          Number(row["Employee Contribution"]) > 0 ||
          Number(row["Employer Contribution"]) > 0
      );

    if (rows.length > 0) {
      const totalEmployee = rows.reduce(
        (sum, row) => sum + Number(row["Employee Contribution"] || 0),
        0
      );
      const totalEmployer = rows.reduce(
        (sum, row) => sum + Number(row["Employer Contribution"] || 0),
        0
      );
      rows.push({
        "Employee Name": "TOTAL",
        "Man Number": "",
        "Employee Contribution": totalEmployee.toFixed(2),
        "Employer Contribution": totalEmployer.toFixed(2),
        Total: (totalEmployee + totalEmployer).toFixed(2),
      });
    }

    return rows;
  };

  // Staff allowances: one row per employee with each allowance name as a column
  const generateStaffAllowancesReport = (payslips: Payslip[]) => {
    const allowanceNames = Array.from(
      new Set(
        payslips.flatMap((payslip) =>
          payslipAllowanceBreakdown(payslip)
            .map((a) => (a.name || "").trim())
            .filter(Boolean)
        )
      )
    ).sort((a, b) => a.localeCompare(b));

    return payslips
      .map((payslip) => {
        const emp = resolvePayslipEmployee(payslip, employees);
        const allowances = payslipAllowanceBreakdown(payslip);
        const amountsByName = new Map<string, number>();
        allowances.forEach((line) => {
          const name = (line.name || "").trim();
          if (!name) return;
          amountsByName.set(
            name,
            (amountsByName.get(name) || 0) + Number(line.amount || 0)
          );
        });

        const row: Record<string, string> = {
          "Man Number": String(emp.manNumber || ""),
          "Employee Name": emp.name,
          Department: emp.division || "",
        };

        let total = 0;
        allowanceNames.forEach((name) => {
          const amount = amountsByName.get(name) || 0;
          total += amount;
          row[name] = amount.toFixed(2);
        });
        row["Total Allowances"] = total.toFixed(2);
        row.Period = payslip.period || selectedMonth;

        return row;
      })
      .filter((row) => Number(row["Total Allowances"] || 0) > 0);
  };

  // Funeral contributions from payslip deduction lines (name contains "funeral")
  const generateFuneralContributionReport = (payslips: Payslip[]) => {
    const rows = payslips
      .map((payslip) => {
        const emp = resolvePayslipEmployee(payslip, employees);
        const funeralLines = payslipDeductionBreakdown(payslip).filter(
          (d) => d.name && d.name.toLowerCase().includes("funeral")
        );
        if (funeralLines.length === 0) return null;

        const employeeContribution = funeralLines.reduce(
          (sum, d) => sum + Number(d.amount || 0),
          0
        );
        const employerContribution = funeralLines.reduce(
          (sum, d) => sum + payslipLineEmployerAmount(d),
          0
        );

        return {
          "Employee Name": emp.name,
          "Man Number": emp.manNumber || "",
          "Employee Contribution": employeeContribution.toFixed(2),
          "Employer Contribution": employerContribution.toFixed(2),
          Total: (employeeContribution + employerContribution).toFixed(2),
        };
      })
      .filter(Boolean) as Record<string, string>[];

    if (rows.length > 0) {
      const totalEmployee = rows.reduce(
        (sum, row) => sum + Number(row["Employee Contribution"] || 0),
        0
      );
      const totalEmployer = rows.reduce(
        (sum, row) => sum + Number(row["Employer Contribution"] || 0),
        0
      );
      rows.push({
        "Employee Name": "TOTAL",
        "Man Number": "",
        "Employee Contribution": totalEmployee.toFixed(2),
        "Employer Contribution": totalEmployer.toFixed(2),
        Total: (totalEmployee + totalEmployer).toFixed(2),
      });
    }

    return rows;
  };

  // Generate ZRA PAYE Schedule
  const generateZraSchedule = (payslips: Payslip[]) => {
    return payslips.map((payslip) => {
      const emp = resolvePayslipEmployee(payslip, employees);
      const paye = payslipDeductionBreakdown(payslip)
        .filter((d) => d.name && d.name.toLowerCase().includes('paye'))
        .reduce((s, d) => s + Number(d.amount || 0), 0);
      return {
        'Man Number': emp.manNumber,
        'Employee Name': emp.name,
        'NRC': emp.nrc,
        'Taxable Income': payslipBasicSalary(payslip),
        'PAYE Tax': paye,
        'Period': payslip.period,
      };
    });
  };

  // Generate Employee Payslips Export
  const generatePayslipsExport = (payslips: Payslip[]) => {
    return payslips.map((payslip) => {
      const emp = resolvePayslipEmployee(payslip, employees);
      return {
        'Man Number': emp.manNumber,
        'Employee Name': emp.name,
        'Department': emp.division,
        'Basic Salary': payslip.basicSalary || 0,
        'Total Allowances': payslip.totalAllowances || 0,
        'Gross Salary': payslip.grossSalary || 0,
        'Total Deductions': payslip.totalDeductions || 0,
        'PAYE Tax': payslipStatutoryAmount(payslip, 'paye'),
        'NAPSA': payslipStatutoryAmount(payslip, 'napsa'),
        'NHIMA': payslipStatutoryAmount(payslip, 'nhima'),
        'Net Salary': payslip.netSalary || 0,
        'Period': payslip.period,
        'Status': payslip.status
      };
    });
  };

  // Generate preview data from actual database data
  const generateDeductionPreviewFromData = (deductionsData: any[], deductionName: string) => {
    // For NAPSA, show both summary and individual transactions
    if (deductionName.toLowerCase().includes('napsa')) {
      const totalEmployeeAmount = deductionsData.reduce((sum, item) => sum + Number(item.amount || 0), 0);
      const employerAmount = totalEmployeeAmount; // Same as employee amount for NAPSA
      const totalAmount = totalEmployeeAmount + employerAmount;
      
      // Create individual employee transactions
      const employeeTransactions = deductionsData.map(item => {
        const employeeContribution = Number(item.amount || 0);
        const employerContribution = employeeContribution; // Same as employee for NAPSA
        const totalContribution = employeeContribution + employerContribution;
        
        return {
          'Man Number': manNumberFromDeductionItem(item),
          'Employee Name': `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Unknown',
          'NRC': item.nrc || 'N/A',
          'Employee Contribution': employeeContribution.toFixed(2),
          'Employer Contribution': employerContribution.toFixed(2),
          'Total Contribution': totalContribution.toFixed(2),
          'Period': item.period || selectedMonth,
          'Status': item.status || 'approved'
        };
      });
      
      // Add total row
      employeeTransactions.push({
        'Man Number': 'TOTAL',
        'Employee Name': '',
        'NRC': '',
        'Employee Contribution': totalEmployeeAmount.toFixed(2),
        'Employer Contribution': employerAmount.toFixed(2),
        'Total Contribution': totalAmount.toFixed(2),
        'Period': selectedMonth,
        'Status': 'Summary'
      });
      
      return {
        summary: {
          'Description': 'NAPSA Retirement Summary',
          'Total Amount from Employees': totalEmployeeAmount.toFixed(2),
          'Amount from Employer': employerAmount.toFixed(2),
          'Total Amount to Remit': totalAmount.toFixed(2),
          'Period': selectedMonth,
          'Status': 'Ready for Remittance'
        },
        transactions: employeeTransactions
      };
    }

    // Non-NAPSA deduction remittance: Employee / Employer / Total
    // NHIMA mirrors employee when no employer_amount snapshot; others use catalog employer %.
    const employeeData = deductionsData.map((item) => {
      const employeeContribution = Number(item.amount || 0);
      let employerContribution = Number(item.employer_amount || 0);
      const lowerItemName = String(item.name || deductionName || '').toLowerCase();
      if (
        employerContribution <= 0 &&
        (lowerItemName.includes('nhima') || lowerItemName.includes('napsa'))
      ) {
        employerContribution = employeeContribution;
      }
      return {
        'Employee Name': `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Unknown',
        'Man Number': manNumberFromDeductionItem(item),
        'Employee Contribution': employeeContribution.toFixed(2),
        'Employer Contribution': employerContribution.toFixed(2),
        Total: (employeeContribution + employerContribution).toFixed(2),
      };
    });

    if (employeeData.length > 0) {
      const totalEmployee = employeeData.reduce(
        (sum, row) => sum + Number(row['Employee Contribution'] || 0),
        0
      );
      const totalEmployer = employeeData.reduce(
        (sum, row) => sum + Number(row['Employer Contribution'] || 0),
        0
      );
      employeeData.push({
        'Employee Name': 'TOTAL',
        'Man Number': '',
        'Employee Contribution': totalEmployee.toFixed(2),
        'Employer Contribution': totalEmployer.toFixed(2),
        Total: (totalEmployee + totalEmployer).toFixed(2),
      });
    }

    return employeeData;
  };

  // Generate Deduction Name Schedule for specific deduction name (legacy function for export)
  const generateDeductionNameSchedule = (payslips: Payslip[], deductionName: string) => {
    // For NAPSA, show retirement summary
    if (deductionName.toLowerCase().includes('napsa')) {
      const totalEmployeeAmount = payslips.reduce((sum, payslip) => {
        const deductionData = (payslip.deductionBreakdown || [])
          .find((d: any) => d.name && d.name.toLowerCase().includes(deductionName.toLowerCase()));
        return sum + (deductionData ? Number(deductionData.amount || 0) : 0);
      }, 0);
      
      const employerAmount = totalEmployeeAmount; // Same as employee amount for NAPSA
      const totalAmount = totalEmployeeAmount + employerAmount;
      
      return [{
        'Description': 'NAPSA Retirement Summary',
        'Total Amount from Employees': totalEmployeeAmount.toFixed(2),
        'Amount from Employer': employerAmount.toFixed(2),
        'Total Amount to Remit': totalAmount.toFixed(2),
        'Period': selectedMonth,
        'Status': 'Ready for Remittance'
      }];
    }
    
    // For other deductions, show individual employee amounts
    const employeeData = payslips.map((payslip) => {
      const emp = resolvePayslipEmployee(payslip, employees);
      const deductionData = payslipDeductionBreakdown(payslip).find(
        (d) => d.name && d.name.toLowerCase().includes(deductionName.toLowerCase())
      );
      
      return {
        'Man Number': emp.manNumber,
        'Employee Name': emp.name,
        'NRC': emp.nrc,
        'Deduction Name': deductionName,
        'Amount': deductionData ? Number(deductionData.amount || 0) : 0,
        'Period': payslip.period,
        'Status': payslip.status
      };
    }).filter(item => item.Amount > 0); // Only include employees with this deduction
    
    // Add total summary at the end for non-NAPSA deductions
    if (employeeData.length > 0) {
      const totalAmount = employeeData.reduce((sum, item) => sum + item.Amount, 0);
      employeeData.push({
        'Man Number': 'TOTAL',
        'Employee Name': '',
        'NRC': '',
        'Deduction Name': deductionName,
        'Amount': totalAmount,
        'Period': selectedMonth,
        'Status': 'approved'
      });
    }
    
    return employeeData;
  };

  const renderApprovalStatus = () => {
    const getStatusLabel = () => {
      switch (payrollData.status) {
        case "draft": return "Draft";
        case "pending_hr": return "Pending HR Review";
        case "pending_finance": return "Pending Finance Review";
        case "pending_audit": return "Pending Audit Review";
        case "pending_final": return "Pending Final Approval";
        case "approved": return "Fully Approved";
        default: return "Unknown";
      }
    };
    
    return (
      <div className="flex items-center space-x-2">
        <span className="font-medium">Status:</span>
        {payrollData.status === "approved" ? (
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
            {getStatusLabel()}
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
            {getStatusLabel()}
          </Badge>
        )}
      </div>
    );
  };

  // Fetch payslips for reports (normalized so employee/bank fields match payslip UI)
  const fetchPayslipsForReport = async (period: string): Promise<Payslip[]> => {
    const cid = exportCouncilId;
    if (cid == null || cid <= 0) {
      return [];
    }
    try {
      const payslips = await PayslipService.getPayslipsByPeriod(period, cid);
      return payslips.filter(
        (p) => p.period === period && (p.status === 'approved' || p.status === 'paid')
      );
    } catch (error) {
      console.error('Error fetching payslips:', error);
      toast({
        title: "Error",
        description: "Failed to fetch payslips for the selected period.",
        variant: "destructive",
      });
      return [];
    }
  };

  // Generate Monthly Payroll Summary Report
  const generateMonthlyPayrollSummary = (payslips: Payslip[]) => {
    const totalBasicSalary = payslips.reduce((sum, p) => sum + (Number(p.basicSalary) || 0), 0);
    const totalAllowances = payslips.reduce((sum, p) => sum + (Number(p.totalAllowances) || 0), 0);
    const totalGrossSalary = payslips.reduce((sum, p) => sum + (Number(p.grossSalary) || 0), 0);
    const totalDeductions = payslips.reduce((sum, p) => sum + (Number(p.totalDeductions) || 0), 0);
    const totalNetSalary = payslips.reduce((sum, p) => sum + (Number(p.netSalary) || 0), 0);
    const totalPAYE = payslips.reduce((sum, p) => sum + payslipStatutoryAmount(p, 'paye'), 0);
    const totalNAPSA = payslips.reduce((sum, p) => sum + payslipStatutoryAmount(p, 'napsa'), 0);
    const totalNHIMA = payslips.reduce((sum, p) => sum + payslipStatutoryAmount(p, 'nhima'), 0);
    const totalEmployerNAPSA = payslips.reduce((sum, p) => sum + payslipEmployerNapsa(p), 0);
    const totalEmployerNHIMA = payslips.reduce((sum, p) => sum + payslipEmployerNhima(p), 0);
    const totalWageBill = payslips.reduce((sum, p) => sum + payslipWageBill(p), 0);

    const summary = {
      'Period': formatPayPeriod(selectedMonth),
      'Total Employees': String(payslips.length),
      'Total Basic Salary': totalBasicSalary.toFixed(2),
      'Total Allowances': totalAllowances.toFixed(2),
      'Total Gross Salary': totalGrossSalary.toFixed(2),
      'Total Deductions': totalDeductions.toFixed(2),
      'Total PAYE': totalPAYE.toFixed(2),
      'Total NAPSA (Employee 5%)': totalNAPSA.toFixed(2),
      'Total Employer NAPSA (5%)': totalEmployerNAPSA.toFixed(2),
      'Total NHIMA (Employee 1%)': totalNHIMA.toFixed(2),
      'Total Employer NHIMA (1%)': totalEmployerNHIMA.toFixed(2),
      'Total Wage Bill': totalWageBill.toFixed(2),
      'Total Net Salary': totalNetSalary.toFixed(2)
    };

    const details = payslips.map((payslip) => {
      const emp = resolvePayslipEmployee(payslip, employees);
      const paye = payslipStatutoryAmount(payslip, 'paye');
      const napsa = payslipStatutoryAmount(payslip, 'napsa');
      const nhima = payslipStatutoryAmount(payslip, 'nhima');
      const employerNapsa = payslipEmployerNapsa(payslip);
      const employerNhima = payslipEmployerNhima(payslip);
      const wageBill = payslipWageBill(payslip);
      return {
        'Man Number': emp.manNumber,
        'Employee Name': emp.name,
        'Department': emp.division,
        'Basic Salary': (Number(payslip.basicSalary) || 0).toFixed(2),
        'Allowances': (Number(payslip.totalAllowances) || 0).toFixed(2),
        'Gross Salary': (Number(payslip.grossSalary) || 0).toFixed(2),
        'PAYE': paye.toFixed(2),
        'NAPSA': napsa.toFixed(2),
        'NHIMA': nhima.toFixed(2),
        'Employer NAPSA': employerNapsa.toFixed(2),
        'Employer NHIMA': employerNhima.toFixed(2),
        'Wage Bill': wageBill.toFixed(2),
        'Deductions': (Number(payslip.totalDeductions) || 0).toFixed(2),
        'Net Salary': (Number(payslip.netSalary) || 0).toFixed(2),
        'Status': payslip.status
      };
    });

    return { summary, details };
  };

  // Generate Employee Earnings Statement
  const generateEmployeeEarningsStatement = (payslips: Payslip[]) => {
    return payslips.map((payslip) => {
      const emp = resolvePayslipEmployee(payslip, employees);
      const allowances = payslipAllowanceBreakdown(payslip);
      const totalAllowances = allowances.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
      
      return {
        'Man Number': emp.manNumber,
        'Employee Name': emp.name,
        'Department': emp.division,
        'Position': emp.jobTitle,
        'Basic Salary': (Number(payslip.basicSalary) || 0).toFixed(2),
        'Allowances Count': allowances.length,
        'Total Allowances': totalAllowances.toFixed(2),
        'Gross Salary': (Number(payslip.grossSalary) || 0).toFixed(2),
        'Net Salary': (Number(payslip.netSalary) || 0).toFixed(2),
        'Period': payslip.period,
        'Status': payslip.status
      };
    });
  };

  // Generate Employee Deductions Statement (line-item deductions per employee)
  const generateEmployeeDeductionsStatement = (payslips: Payslip[]) => {
    const rows: Record<string, string>[] = [];

    payslips.forEach((payslip) => {
      const emp = resolvePayslipEmployee(payslip, employees);
      const manNumber = emp.manNumber;
      const employeeName = emp.name;
      const department = emp.division;
      const totalDeductions = (Number(payslip.totalDeductions) || 0).toFixed(2);
      const period = payslip.period || selectedMonth;
      const deductions = payslipDeductionBreakdown(payslip);

      if (deductions.length === 0) {
        rows.push({
          'Man Number': String(manNumber),
          'Employee Name': employeeName,
          Department: department,
          'Deduction Name': '—',
          'Deduction Amount': '0.00',
          'Total Deductions': totalDeductions,
          Period: period,
          Status: String(payslip.status || ''),
        });
        return;
      }

      deductions.forEach((deduction: { name?: string; amount?: number }) => {
        rows.push({
          'Man Number': String(manNumber),
          'Employee Name': employeeName,
          Department: department,
          'Deduction Name': deduction.name || 'Deduction',
          'Deduction Amount': Number(deduction.amount || 0).toFixed(2),
          'Total Deductions': totalDeductions,
          Period: period,
          Status: String(payslip.status || ''),
        });
      });
    });

    return rows;
  };

  // Generate PAYE & Statutory Contributions Report
  const generatePAYEStatutoryReport = (payslips: Payslip[]) => {
    return payslips.map((payslip) => {
      const emp = resolvePayslipEmployee(payslip, employees);
      const breakdown = payslipDeductionBreakdown(payslip);
      const paye = breakdown
        .filter((d) => d.name && d.name.toLowerCase().includes('paye'))
        .reduce((s, d) => s + Number(d.amount || 0), 0);
      const napsa = breakdown
        .filter((d) => d.name && d.name.toLowerCase().includes('napsa'))
        .reduce((s, d) => s + Number(d.amount || 0), 0);
      const nhima = breakdown
        .filter((d) => d.name && d.name.toLowerCase().includes('nhima'))
        .reduce((s, d) => s + Number(d.amount || 0), 0);
      
      return {
        'Man Number': emp.manNumber,
        'Employee Name': emp.name,
        'NRC': emp.nrc,
        'TPIN': emp.taxId,
        'Taxable Income': (Number(payslip.basicSalary) || 0).toFixed(2),
        'PAYE Tax': paye.toFixed(2),
        'NAPSA Contribution': napsa.toFixed(2),
        'NHIMA Contribution': nhima.toFixed(2),
        'Total Statutory Deductions': (paye + napsa + nhima).toFixed(2),
        'Period': payslip.period
      };
    });
  };

  // Generate NAPSA Return (tailored row-per-employee format)
  const generateNapsaReturnFormat = (payslips: Payslip[]) => {
    return payslips.map((payslip) => {
      const emp = resolvePayslipEmployee(payslip, employees);
      const napsa = payslipDeductionBreakdown(payslip)
        .filter((d) => d.name && d.name.toLowerCase().includes('napsa'))
        .reduce((s, d) => s + Number(d.amount || 0), 0);
      return {
        'Scheme': 'NAPSA',
        'Man Number': emp.manNumber,
        'Employee Name': emp.name,
        'NRC': emp.nrc,
        'NAPSA No': '',
        'Basic Salary': (Number(payslip.basicSalary) || 0).toFixed(2),
        'Employee Contribution': napsa.toFixed(2),
        'Employer Contribution': napsa.toFixed(2),
        'Total Contribution': (napsa * 2).toFixed(2),
        Period: payslip.period || selectedMonth,
      };
    });
  };

  // Generate ZRA PAYE Return (tailored row-per-employee format)
  const generateZraReturnFormat = (payslips: Payslip[]) => {
    return payslips.map((payslip) => {
      const emp = resolvePayslipEmployee(payslip, employees);
      const paye = payslipDeductionBreakdown(payslip)
        .filter((d) => d.name && d.name.toLowerCase().includes('paye'))
        .reduce((s, d) => s + Number(d.amount || 0), 0);
      const gross = Number(payslip.grossSalary) || 0;
      const employmentNature = emp.employee?.status === 'on_leave' ? 'Temporary' : 'Permanent';
      return {
        'Identity type (A)': 'TPIN',
        'Identity type (TPIN)': emp.taxId || '',
        'Name of Employee (C)': emp.name,
        'Nature of Employment (Temporary) (Permanent) (D)': employmentNature,
        'Gross emoluments for the Tax Period (E)': gross.toFixed(4),
        'Chargeable emoluments for the tax period (F)': gross.toFixed(4),
        'Total tax credit for the tax period (G)': paye.toFixed(4),
        'Tax Deducted as shown on the TRD for the tax period (H)': paye.toFixed(4),
        'Tax Adjusted in the tax period (I)': '0',
      };
    });
  };

  // Generate Variance Report (Current vs Previous)
  const generateVarianceReport = async (currentPeriod: string) => {
    // Calculate previous month
    const currentDate = new Date(currentPeriod + '-01');
    const previousDate = new Date(currentDate);
    previousDate.setMonth(previousDate.getMonth() - 1);
    const previousPeriod = previousDate.toISOString().substring(0, 7);

    const [currentPayslips, previousPayslips] = await Promise.all([
      fetchPayslipsForReport(currentPeriod),
      fetchPayslipsForReport(previousPeriod)
    ]);

    const varianceData = currentPayslips.map((currentPayslip) => {
      const emp = resolvePayslipEmployee(currentPayslip, employees);
      const previousPayslip = previousPayslips.find((p) =>
        matchPayslipToPrevious(currentPayslip, p, employees)
      );
      
      const currentNet = payslipNetSalary(currentPayslip);
      const previousNet = previousPayslip ? payslipNetSalary(previousPayslip) : 0;
      const variance = currentNet - previousNet;
      const variancePercent = previousNet > 0 ? ((variance / previousNet) * 100) : 0;

      return {
        'Man Number': emp.manNumber,
        'Employee Name': emp.name,
        'Previous Period Net': previousNet.toFixed(2),
        'Current Period Net': currentNet.toFixed(2),
        'Variance Amount': variance.toFixed(2),
        'Variance %': variancePercent.toFixed(2) + '%',
        'Current Period': currentPeriod,
        'Previous Period': previousPeriod
      };
    });

    return varianceData;
  };

  // Handler for Monthly Payroll Summary
  const handleMonthlyPayrollSummary = async () => {
    setLoadingReports(true);
    try {
      const payslips = await fetchPayslipsForReport(selectedMonth);
      if (payslips.length === 0) {
        toast({
          title: "No Data",
          description: `No approved payslips found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }
      const reportData = generateMonthlyPayrollSummary(payslips);
      setExportPreview(reportData);
      setExportType("Monthly Payroll Summary");
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: "Error",
        description: "Failed to generate Monthly Payroll Summary.",
        variant: "destructive",
      });
    } finally {
      setLoadingReports(false);
    }
  };

  // Handler for Employee Earnings Statement
  const handleEmployeeEarningsStatement = async () => {
    setLoadingReports(true);
    try {
      const payslips = await fetchPayslipsForReport(selectedMonth);
      if (payslips.length === 0) {
        toast({
          title: "No Data",
          description: `No approved payslips found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }
      const reportData = generateEmployeeEarningsStatement(payslips);
      setExportPreview(reportData);
      setExportType("Employee Earnings Statement");
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: "Error",
        description: "Failed to generate Employee Earnings Statement.",
        variant: "destructive",
      });
    } finally {
      setLoadingReports(false);
    }
  };

  // Handler for Employee Deductions Statement
  const handleEmployeeDeductionsStatement = async () => {
    setLoadingReports(true);
    try {
      const payslips = await fetchPayslipsForReport(selectedMonth);
      if (payslips.length === 0) {
        toast({
          title: "No Data",
          description: `No approved payslips found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }
      const reportData = generateEmployeeDeductionsStatement(payslips);
      setExportPreview(reportData);
      setExportType("Employee Deductions Statement");
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: "Error",
        description: "Failed to generate Employee Deductions Statement.",
        variant: "destructive",
      });
    } finally {
      setLoadingReports(false);
    }
  };

  // Handler for PAYE & Statutory Contributions Report
  const handlePAYEStatutoryReport = async () => {
    setLoadingReports(true);
    try {
      const payslips = await fetchPayslipsForReport(selectedMonth);
      if (payslips.length === 0) {
        toast({
          title: "No Data",
          description: `No approved payslips found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }
      const reportData = generatePAYEStatutoryReport(payslips);
      setExportPreview(reportData);
      setExportType("PAYE & Statutory Contributions Report");
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: "Error",
        description: "Failed to generate PAYE & Statutory Contributions Report.",
        variant: "destructive",
      });
    } finally {
      setLoadingReports(false);
    }
  };

  const handleNapsaReturnReport = async () => {
    setLoadingReports(true);
    try {
      const payslips = await fetchPayslipsForReport(selectedMonth);
      if (payslips.length === 0) {
        toast({
          title: "No Data",
          description: `No approved payslips found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }
      const reportData = generateNapsaReturnFormat(payslips);
      setExportPreview(reportData);
      setExportType("NAPSA Return");
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating NAPSA return:', error);
      toast({
        title: "Error",
        description: "Failed to generate NAPSA Return.",
        variant: "destructive",
      });
    } finally {
      setLoadingReports(false);
    }
  };

  const handleZraReturnReport = async () => {
    setLoadingReports(true);
    try {
      const payslips = await fetchPayslipsForReport(selectedMonth);
      if (payslips.length === 0) {
        toast({
          title: "No Data",
          description: `No approved payslips found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }
      const reportData = generateZraReturnFormat(payslips);
      setExportPreview(reportData);
      setExportType("ZRA PAYE Return");
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating ZRA return:', error);
      toast({
        title: "Error",
        description: "Failed to generate ZRA PAYE Return.",
        variant: "destructive",
      });
    } finally {
      setLoadingReports(false);
    }
  };

  // Handler for Payflex Report
  const handlePayflexReport = async () => {
    setLoadingReports(true);
    try {
      const payslips = await fetchPayslipsForReport(selectedMonth);
      if (payslips.length === 0) {
        toast({
          title: "No Data",
          description: `No approved payslips found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }
      const reportData = generatePayflexReport(payslips);
      setExportPreview(reportData);
      setExportType("Payflex Report");
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating Payflex report:', error);
      toast({
        title: "Error",
        description: "Failed to generate Payflex Report.",
        variant: "destructive",
      });
    } finally {
      setLoadingReports(false);
    }
  };

  // Handler for Variance Report
  const handleVarianceReport = async () => {
    setLoadingReports(true);
    try {
      const reportData = await generateVarianceReport(selectedMonth);
      if (reportData.length === 0) {
        toast({
          title: "No Data",
          description: `No data available for variance comparison.`,
          variant: "destructive",
        });
        return;
      }
      setExportPreview(reportData);
      setExportType("Variance Report (Current vs. Previous)");
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: "Error",
        description: "Failed to generate Variance Report.",
        variant: "destructive",
      });
    } finally {
      setLoadingReports(false);
    }
  };

  const handleStaffAllowancesReport = async () => {
    setLoadingReports(true);
    try {
      const payslips = await fetchPayslipsForReport(selectedMonth);
      if (payslips.length === 0) {
        toast({
          title: "No Data",
          description: `No approved payslips found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }
      const reportData = generateStaffAllowancesReport(payslips);
      if (reportData.length === 0) {
        toast({
          title: "No Data",
          description: `No staff allowances found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }
      setExportPreview(reportData);
      setExportType("Staff Allowances Report");
      setShowPreview(true);
    } catch (error) {
      console.error("Error generating staff allowances report:", error);
      toast({
        title: "Error",
        description: "Failed to generate Staff Allowances Report.",
        variant: "destructive",
      });
    } finally {
      setLoadingReports(false);
    }
  };

  const handleNhimaContributionsReport = async () => {
    setLoadingReports(true);
    try {
      const payslips = await fetchPayslipsForReport(selectedMonth);
      if (payslips.length === 0) {
        toast({
          title: "No Data",
          description: `No approved payslips found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }
      const reportData = generateNhimaSchedule(payslips);
      if (reportData.length === 0) {
        toast({
          title: "No Data",
          description: `No NHIMA deductions found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }
      setExportPreview(reportData);
      setExportType("NHIMA Contributions Report");
      setShowPreview(true);
    } catch (error) {
      console.error("Error generating NHIMA report:", error);
      toast({
        title: "Error",
        description: "Failed to generate NHIMA Contributions Report.",
        variant: "destructive",
      });
    } finally {
      setLoadingReports(false);
    }
  };

  const handleFuneralContributionsReport = async () => {
    setLoadingReports(true);
    try {
      const payslips = await fetchPayslipsForReport(selectedMonth);
      if (payslips.length === 0) {
        toast({
          title: "No Data",
          description: `No approved payslips found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }
      const reportData = generateFuneralContributionReport(payslips);
      if (reportData.length === 0) {
        toast({
          title: "No Data",
          description: `No funeral deductions found for ${formatPayPeriod(selectedMonth)}.`,
          variant: "destructive",
        });
        return;
      }
      setExportPreview(reportData);
      setExportType("Funeral Contributions Report");
      setShowPreview(true);
    } catch (error) {
      console.error("Error generating funeral report:", error);
      toast({
        title: "Error",
        description: "Failed to generate Funeral Contributions Report.",
        variant: "destructive",
      });
    } finally {
      setLoadingReports(false);
    }
  };

  const getExportPreviewTitle = () => {
    const period = formatPayPeriod(selectedMonth);
    switch (exportType) {
      case "Monthly Payroll Summary":
        return `Monthly Payroll Summary - ${period}`;
      case "Employee Earnings Statement":
        return `Employee Earnings Statement - ${period}`;
      case "Employee Deductions Statement":
        return `Employee Deductions Statement - ${period}`;
      case "PAYE & Statutory Contributions Report":
        return `PAYE & Statutory Contributions Report - ${period}`;
      case "NAPSA Return":
        return `NAPSA Return - ${period}`;
      case "ZRA PAYE Return":
        return `ZRA PAYE Return - ${period}`;
      case "Payflex Report":
        return `Payflex Report - ${period}`;
      case "Variance Report (Current vs. Previous)":
        return `Variance Report - ${period}`;
      case "Staff Allowances Report":
        return `Staff Allowances Report - ${period}`;
      case "NHIMA Contributions Report":
        return `NHIMA Contributions Report - ${period}`;
      case "Funeral Contributions Report":
        return `Funeral Contributions Report - ${period}`;
      default:
        return exportType.toLowerCase().includes("napsa")
          ? `NAPSA Retirement Report - ${period}`
          : `Export Preview - ${exportType} Deduction for ${period}`;
    }
  };

  const handlePrintPreview = () => {
    const content = document.getElementById("export-preview-content");
    if (!content) {
      toast({
        title: "Print unavailable",
        description: "Preview content could not be found.",
        variant: "destructive",
      });
      return;
    }
    const printWindow = window.open("", "_blank", "noopener,noreferrer,width=1024,height=768");
    if (!printWindow) {
      toast({
        title: "Pop-up blocked",
        description: "Allow pop-ups to print this report.",
        variant: "destructive",
      });
      return;
    }
    printWindow.document.write(`<!DOCTYPE html><html><head><title>${getExportPreviewTitle()}</title>
<style>
  body { font-family: Arial, sans-serif; padding: 16px; color: #111; }
  h1 { font-size: 18px; margin: 0 0 12px; }
  table { border-collapse: collapse; width: 100%; font-size: 11px; }
  th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; font-weight: 600; }
  .bg-blue-50, .bg-gray-100 { background: #f9fafb !important; }
  @media print { body { padding: 0; } }
</style></head><body>
<h1>${getExportPreviewTitle()}</h1>
${content.innerHTML}
</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleExportPdf = async () => {
    const content = document.getElementById("export-preview-content");
    if (!content) {
      toast({
        title: "PDF unavailable",
        description: "Preview content could not be found.",
        variant: "destructive",
      });
      return;
    }
    try {
      setGeneratingExport(true);
      const html2pdf = (await import("html2pdf.js")).default as any;
      const filename = `${getExportPreviewFileBase()}.pdf`;
      await html2pdf()
        .set({
          margin: [0.4, 0.4, 0.4, 0.4],
          filename,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: "#ffffff",
          },
          jsPDF: { unit: "in", format: "a4", orientation: "landscape", compress: true },
          pagebreak: { mode: ["avoid-all", "css", "legacy"] },
        })
        .from(content)
        .save();
      toast({
        title: "PDF exported",
        description: `${filename} has been downloaded.`,
      });
    } catch (error) {
      console.error("PDF export failed:", error);
      toast({
        title: "PDF export failed",
        description: "Could not generate PDF. Try Print and save as PDF instead.",
        variant: "destructive",
      });
    } finally {
      setGeneratingExport(false);
    }
  };

  // Check if there are approved payslips for the selected month
  const hasApprovedPayslips = async (period: string): Promise<boolean> => {
    try {
      const payslips = await fetchPayslipsForReport(period);
      console.log('payslips', payslips);
      return payslips.length > 0;
    } catch {
      return false;
    }
  };

  // State to track if reports are available
  const [reportsAvailable, setReportsAvailable] = useState(false);

  // Check for approved payslips when selectedMonth changes
  useEffect(() => {
    const checkReportsAvailability = async () => {
      const available = await hasApprovedPayslips(selectedMonth);
      console.log('available', available);
      setReportsAvailable(available);
    };
    checkReportsAvailability();
  }, [selectedMonth, exportCouncilId]);

  

  const handleToggleAllEmployees = (checked: boolean) => {
    const updatedSelection = Object.keys(selectedEmployees).reduce((acc, empId) => {
      acc[empId] = checked;
      return acc;
    }, {} as Record<string, boolean>);
    
    setSelectedEmployees(updatedSelection);
  };

  const handleToggleEmployee = (employeeId: string, checked: boolean) => {
    setSelectedEmployees(prev => ({
      ...prev,
      [employeeId]: checked
    }));
  };

  const getSelectedEmployeeCount = () => {
    return Object.values(selectedEmployees).filter(Boolean).length;
  };

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`w-full grid ${approvalOnlyPayroll ? 'grid-cols-1' : 'grid-cols-3'}`}>
          {canRunPayroll && <TabsTrigger value="process">Process</TabsTrigger>}
          <TabsTrigger value="approval">Approval</TabsTrigger>
          {canRunPayroll && <TabsTrigger value="export">Export</TabsTrigger>}
        </TabsList>
        
        {canRunPayroll && <TabsContent value="process">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Process Payroll</h3>
            
            <div className="flex items-center space-x-4 mb-4">
              <Button 
                variant={processingMode === "batch" ? "default" : "outline"}
                size="sm"
                onClick={() => setProcessingMode("batch")}
                className="flex-1"
              >
                <Users className="mr-2 h-4 w-4" />
                Batch Processing
              </Button>
              <Button 
                variant={processingMode === "individual" ? "default" : "outline"}
                size="sm"
                onClick={() => setProcessingMode("individual")}
                className="flex-1"
              >
                <User className="mr-2 h-4 w-4" />
                Individual Processing
              </Button>
            </div>
            
            <div className="flex items-center space-x-4 mb-4">
              <Calendar className="h-5 w-5 text-gray-500" />
              <Input 
                type="month" 
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="flex-1"
              />
            </div>
            
            {processingMode === "individual" ? (
              <div className="space-y-4">
                <Label htmlFor="employeeSelect">Select Employee</Label>
                <Select
                  value={selectedEmployeeId}
                  onValueChange={setSelectedEmployeeId}
                >
                  <SelectTrigger id="employeeSelect">
                    <SelectValue placeholder="Select an employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.firstName} {emp.lastName} - {emp.employeeId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <Card className="mb-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex justify-between">
                    <span>Select Employees</span>
                    <div className="flex items-center text-xs space-x-2">
                      <Checkbox 
                        id="selectAll" 
                        checked={getSelectedEmployeeCount() === employees.length}
                        onCheckedChange={(checked) => handleToggleAllEmployees(!!checked)}
                      />
                      <label htmlFor="selectAll">Select All</label>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-48">
                    <div className="space-y-2">
                      {employees.map(emp => (
                        <div key={emp.id} className="flex items-center space-x-2">
                          <Checkbox 
                            id={`emp-${emp.id}`}
                            checked={selectedEmployees[emp.id] || false}
                            onCheckedChange={(checked) => handleToggleEmployee(emp.id, !!checked)}
                          />
                          <label htmlFor={`emp-${emp.id}`} className="text-sm">
                            {emp.firstName} {emp.lastName} - {emp.employeeId}
                          </label>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  <div className="mt-2 text-sm text-gray-500">
                    {getSelectedEmployeeCount()} of {employees.length} employees selected
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card className="mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Data To Be Processed</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  <li className="flex justify-between">
                    <span className="text-gray-500">Basic Salary:</span>
                    <span>Auto-fetched from employee records</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-500">Allowances:</span>
                    <span>Auto-calculated based on employee entitlements</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-500">Deductions:</span>
                    <span>Statutory + loan repayments + others</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-500">Auto-calculations:</span>
                    <span>Gross pay, taxable income, PAYE, net pay</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={() => refresh.all()}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Data
              </Button>
              
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={processingPayroll || (processingMode === "individual" && !selectedEmployeeId) || 
                          (processingMode === "batch" && getSelectedEmployeeCount() === 0)}
                onClick={handleProcessPayroll}
              >
                {processingPayroll ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Process Payroll
                  </>
                )}
              </Button>
            </div>
          </div>
        </TabsContent>}
        
        <TabsContent value="approval">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Approval Workflow</h3>
              {renderApprovalStatus()}
            </div>
            {/* Month Selector for Approval Tab */}
            <div className="flex flex-wrap items-center gap-4 mb-2">
              <div className="flex items-center space-x-4">
                <Label htmlFor="approvalMonth">Payroll Month:</Label>
                <Input
                  id="approvalMonth"
                  type="month"
                  value={approvalMonth}
                  onChange={e => setApprovalMonth(e.target.value)}
                  className="w-48"
                />
              </div>
              {approvalPayslips.length > 0 && (
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/payroll/review?period=${approvalMonth}`}>
                    <FileText className="h-4 w-4 mr-2" />
                    Review &amp; amend payroll
                  </Link>
                </Button>
              )}
            </div>
            <Card className="mb-4">
              <CardContent className="pt-6">
               
                
                {fetchingApprovalPayslips ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-payroll-primary"></div>
                  </div>
                ) : approvalPayslips.length === 0 ? (
                  <div className="text-center text-gray-500">No payroll processed for this month.</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm font-medium">Period</p>
                      <p className="text-gray-600">{formatPayPeriod(approvalMonth)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Employees</p>
                      <p className="text-gray-600">{approvalPayslips.length}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Allowances</p>
                      <p className="text-gray-600">
                        {formatMoney(
                          approvalPayslips.reduce(
                            (sum, p) => sum + Number(p.totalAllowances ?? (p as any).total_allowances ?? 0),
                            0
                          )
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Deductions</p>
                      <p className="text-gray-600">
                        {formatMoney(
                          approvalPayslips.reduce(
                            (sum, p) => sum + Number(p.totalDeductions ?? (p as any).total_deductions ?? 0),
                            0
                          )
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Gross Pay</p>
                      <p className="text-gray-600">
                        {formatMoney(
                          approvalPayslips.reduce(
                            (sum, p) =>
                              sum +
                              Number(p.basicSalary ?? (p as any).basic_salary ?? 0) +
                              Number(p.totalAllowances ?? (p as any).total_allowances ?? 0),
                            0
                          )
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Net Pay</p>
                      <p className="text-gray-600">
                        {formatMoney(
                          approvalPayslips.reduce((sum, p) => sum + Number(p.netSalary ?? (p as any).net_salary ?? 0), 0)
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Wage Bill</p>
                      <p className="text-gray-600">
                        {formatMoney(
                          approvalPayslips.reduce(
                            (sum, p) => sum + payslipWageBill(p),
                            0
                          )
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Gross + employer NAPSA (5%) + employer NHIMA (1%)
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {approvalPayslips.length > 0 && (
              <Card className="mb-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Batch Pending Approval Details</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Allowance badges show <span className="text-slate-700">ZRA-exempt</span> allowances only (PAYE/taxable items are hidden).
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead>Allowances (PAYE)</TableHead>
                          <TableHead className="text-right">Leave bal.</TableHead>
                          <TableHead className="text-right">Basic</TableHead>
                          <TableHead className="text-right">Allowances</TableHead>
                          <TableHead className="text-right">Gross</TableHead>
                          <TableHead className="text-right">Deductions</TableHead>
                          <TableHead className="text-right">Net</TableHead>
                          <TableHead className="text-right">Wage Bill</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {approvalPayslips.map((payslip, idx) => {
                          const gross =
                            Number(payslip.basicSalary ?? (payslip as any).basic_salary ?? 0) +
                            Number(payslip.totalAllowances ?? (payslip as any).total_allowances ?? 0);
                          const wageBill = payslipWageBill(payslip);
                          const leaveDays = Number(
                            payslip.closingLeaveDays ?? (payslip as any).closing_leave_days ?? 0
                          );
                          return (
                            <TableRow key={String(payslip.id ?? (payslip as any).p_id ?? idx)}>
                              <TableCell>
                                {(payslip as any).firstName && (payslip as any).lastName
                                  ? `${(payslip as any).firstName} ${(payslip as any).lastName}`
                                  : payslip.employeeName || payslip.employeeId || 'Employee'}
                              </TableCell>
                              <TableCell>
                                <AllowancePayeBadges payslip={payslip} compact />
                              </TableCell>
                              <TableCell className="text-right text-sm">
                                {leaveDays > 0 ? `${leaveDays.toFixed(1)} days` : '—'}
                              </TableCell>
                              <TableCell className="text-right">{formatMoney(Number(payslip.basicSalary ?? (payslip as any).basic_salary ?? 0))}</TableCell>
                              <TableCell className="text-right">{formatMoney(Number(payslip.totalAllowances ?? (payslip as any).total_allowances ?? 0))}</TableCell>
                              <TableCell className="text-right">{formatMoney(gross)}</TableCell>
                              <TableCell className="text-right">{formatMoney(Number(payslip.totalDeductions ?? (payslip as any).total_deductions ?? 0))}</TableCell>
                              <TableCell className="text-right font-medium">{formatMoney(Number(payslip.netSalary ?? (payslip as any).net_salary ?? 0))}</TableCell>
                              <TableCell className="text-right font-medium">{formatMoney(wageBill)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
            
        
{approvalStatus && (
  <Card className="mb-6 shadow-md rounded-2xl border">
    <CardContent className="p-4">
      <h3 className="text-lg font-semibold mb-1">Approval Workflow</h3>
      <p className="text-xs text-muted-foreground mb-3">
        Order: HR → Finance → Audit → Secretary
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* 1. HR */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium">1. HR Approval</span>
            <div className="text-xs text-gray-500">
              {approvalSignOffLabels.hr}
            </div>
          </div>
          {approvalStatus.hr_approved_by_name ? (
            <CheckCircle className="text-green-500 h-5 w-5" />
          ) : (
            <Clock className="text-gray-400 h-5 w-5" />
          )}
        </div>

        {/* 2. Finance */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium">2. Finance Approval</span>
            <div className="text-xs text-gray-500">
              {approvalSignOffLabels.finance}
            </div>
          </div>
          {approvalStatus.finance_approved_by_name ? (
            <CheckCircle className="text-green-500 h-5 w-5" />
          ) : (
            <Clock className="text-gray-400 h-5 w-5" />
          )}
        </div>

        {/* 3. Audit */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium">3. Audit Authorization</span>
            <div className="text-xs text-gray-500">
              {approvalSignOffLabels.audit}
            </div>
          </div>
          {approvalStatus.audit_approved_by_name ? (
            <CheckCircle className="text-green-500 h-5 w-5" />
          ) : (
            <Clock className="text-gray-400 h-5 w-5" />
          )}
        </div>

        {/* 4. Secretary */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium">4. Secretary Approval</span>
            <div className="text-xs text-gray-500">
              {approvalSignOffLabels.secretary}
            </div>
          </div>
          {approvalStatus.secretary_approved_by_name ? (
            <CheckCircle className="text-green-500 h-5 w-5" />
          ) : (
            <Clock className="text-gray-400 h-5 w-5" />
          )}
        </div>
      </div>

      {/* Approve Button */}
      {user &&
        approvalStatus?.status &&
        canApprovePayrollStep(userRole, approvalStatus.status) && (
        <Button
          className="mt-6 w-full sm:w-auto"
          onClick={handleApproveStep}
          disabled={approving}
        >
          {approving
            ? "Approving..."
            : `Approve as ${getRoleLabel(userRole)}`}
        </Button>
      )}

      {/* Current Status */}
      <div className="mt-4 text-xs text-gray-600">
        Current Status:{" "}
        <Badge variant="outline" className="ml-1">
          {approvalStatus.status}
        </Badge>
      </div>
    </CardContent>
  </Card>
)}

           
          </div>
        </TabsContent>
        
        {canRunPayroll && <TabsContent value="export">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Export & Reports</h3>
            
            <div className="space-y-3">
              <Label htmlFor="exportType">Select Deduction Type Only</Label>
              <Select value={exportType} onValueChange={setExportType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select deduction type" />
                </SelectTrigger>
                <SelectContent>
                  {uniqueDeductionNames.map((deduction) => (
                    <SelectItem key={deduction.name} value={deduction.name}>
                      {deduction.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Deductions are selected here. Payroll summary and statutory reports are listed separately under Available Reports.
              </p>
              
              <div className="flex items-center space-x-4 pt-2">
                <Label htmlFor="exportMonth" className="whitespace-nowrap">Period:</Label>
                <Input 
                  id="exportMonth"
                  type="month" 
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="flex-1"
                />
              </div>
              
              <div className="flex space-x-2">
                <Button 
                  variant="outline"
                  className="flex-1" 
                  onClick={generatePreview}
                  disabled={generatingExport}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Preview
                </Button>
                
                <Button 
                  className="flex-1" 
                  onClick={handleGenerateExport}
                  disabled={generatingExport}
                >
                  {generatingExport ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Generate & Download
                    </>
                  )}
                </Button>
              </div>
              
              {payrollData.status !== "approved" && (
                <p className="text-sm text-yellow-600 text-center">
                  Note: Exports will include all processed payslips for the selected period
                </p>
              )}
            </div>
            
            <Card className="mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Available Reports</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start" 
                      disabled={!reportsAvailable || loadingReports}
                      onClick={handleMonthlyPayrollSummary}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Monthly Payroll Summary
                    </Button>
                  </li>
                  <li>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start" 
                      disabled={!reportsAvailable || loadingReports}
                      onClick={handleEmployeeEarningsStatement}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Employee Earnings Statement
                    </Button>
                  </li>
                  <li>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start" 
                      disabled={!reportsAvailable || loadingReports}
                      onClick={handleEmployeeDeductionsStatement}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Employee Deductions Statement
                    </Button>
                  </li>
                  <li>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start" 
                      disabled={!reportsAvailable || loadingReports}
                      onClick={handlePAYEStatutoryReport}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      PAYE & Statutory Contributions Report
                    </Button>
                  </li>
                  <li>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      disabled={!reportsAvailable || loadingReports}
                      onClick={handleNapsaReturnReport}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      NAPSA Return
                    </Button>
                  </li>
                  <li>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      disabled={!reportsAvailable || loadingReports}
                      onClick={handleZraReturnReport}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      ZRA PAYE Return
                    </Button>
                  </li>
                  <li>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start" 
                      disabled={!reportsAvailable || loadingReports}
                      onClick={handlePayflexReport}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Payflex Report
                    </Button>
                  </li>
                  <li>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start" 
                      disabled={!reportsAvailable || loadingReports}
                      onClick={handleVarianceReport}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Variance Report (Current vs. Previous)
                    </Button>
                  </li>
                  <li>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      disabled={!reportsAvailable || loadingReports}
                      onClick={handleStaffAllowancesReport}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Staff Allowances Report
                    </Button>
                  </li>
                  <li>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      disabled={!reportsAvailable || loadingReports}
                      onClick={handleNhimaContributionsReport}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      NHIMA Contributions Report
                    </Button>
                  </li>
                  <li>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      disabled={!reportsAvailable || loadingReports}
                      onClick={handleFuneralContributionsReport}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Funeral Contributions Report
                    </Button>
                  </li>
                </ul>
                {!reportsAvailable && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    No approved payslips available for {formatPayPeriod(selectedMonth)}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Export Preview Dialog */}
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
              <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{getExportPreviewTitle()}</DialogTitle>
                </DialogHeader>
                
                {(Array.isArray(exportPreview) && exportPreview.length > 0) || (exportPreview && ((exportPreview.summary && exportPreview.transactions) || (exportPreview.summary && exportPreview.details))) ? (
                  <div className="space-y-4">
                    <div id="export-preview-content" className="space-y-4">
                    {/* Special display for Monthly Payroll Summary */}
                    {exportType === "Monthly Payroll Summary" && exportPreview.summary && exportPreview.details ? (
                      <div className="space-y-4">
                        {/* Summary Card */}
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                          <h4 className="font-semibold text-blue-900 mb-2">Payroll Summary</h4>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                            {Object.entries(exportPreview.summary).map(([key, value]) => (
                              <div key={key}>
                                <span className="font-medium text-gray-700">{key}:</span>
                                <div className="text-lg font-bold text-blue-600">
                                  {formatPreviewCell(value, key)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Employee Details Table */}
                        <div className="space-y-2">
                          <h5 className="font-semibold text-gray-700">Employee Details</h5>
                          <div className="border rounded-lg overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  {exportPreview.details.length > 0 ? Object.keys(exportPreview.details[0] || {}).map((header) => (
                                    <TableHead key={header} className="font-medium">
                                      {header}
                                    </TableHead>
                                  )) : null}
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {exportPreview.details.map((row: any, index: number) => (
                                  <TableRow key={index}>
                                    {Object.entries(row).map(([header, value]: [string, any], cellIndex: number) => (
                                      <TableCell key={cellIndex} className="text-sm">
                                        {formatPreviewCell(value, header)}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </div>
                    ) : exportType.toLowerCase().includes('napsa') && exportPreview.summary ? (
                      <div className="space-y-4">
                        {/* Summary Card */}
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                          <h4 className="font-semibold text-blue-900 mb-2">NAPSA Retirement Summary</h4>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium">Total from Employees:</span>
                              <span className="ml-2 text-lg font-bold text-green-600">
                                {formatPreviewCell(exportPreview.summary['Total Amount from Employees'], 'Total Amount from Employees')}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium">Employer Contribution:</span>
                              <span className="ml-2 text-lg font-bold text-green-600">
                                {formatPreviewCell(exportPreview.summary['Amount from Employer'], 'Amount from Employer')}
                              </span>
                            </div>
                            <div className="col-span-2 border-t pt-2">
                              <span className="font-medium text-lg">Total to Remit:</span>
                              <span className="ml-2 text-xl font-bold text-blue-600">
                                {formatPreviewCell(exportPreview.summary['Total Amount to Remit'], 'Total Amount to Remit')}
                              </span>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-gray-600">
                            Period: {exportPreview.summary['Period']} | Status: {exportPreview.summary['Status']}
                          </div>
                        </div>
                        
                        {/* Individual Transactions Table */}
                        <div className="space-y-2">
                          <h5 className="font-semibold text-gray-700">Individual Employee Contributions</h5>
                          <div className="border rounded-lg overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="font-medium">Man Number</TableHead>
                                  <TableHead className="font-medium">Employee Name</TableHead>
                                  <TableHead className="font-medium">NRC</TableHead>
                                  <TableHead className="font-medium text-right">Employee Contribution</TableHead>
                                  <TableHead className="font-medium text-right">Employer Contribution</TableHead>
                                  <TableHead className="font-medium text-right">Total Contribution</TableHead>
                                  <TableHead className="font-medium">Status</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {exportPreview.transactions.map((row, index) => (
                                  <TableRow key={index} className={row['Man Number'] === 'TOTAL' ? 'bg-gray-100 font-semibold' : ''}>
                                    <TableCell className="text-sm">{row['Man Number']}</TableCell>
                                    <TableCell className="text-sm">{row['Employee Name']}</TableCell>
                                    <TableCell className="text-sm">{row['NRC']}</TableCell>
                                    <TableCell className="text-sm text-right">{formatPreviewCell(row['Employee Contribution'], 'Employee Contribution')}</TableCell>
                                    <TableCell className="text-sm text-right">{formatPreviewCell(row['Employer Contribution'], 'Employer Contribution')}</TableCell>
                                    <TableCell className="text-sm text-right font-medium">{formatPreviewCell(row['Total Contribution'], 'Total Contribution')}</TableCell>
                                    <TableCell className="text-sm">{row['Status']}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="text-sm text-gray-600">
                          Showing {Array.isArray(exportPreview) ? exportPreview.length : 0} record(s) for {exportType}
                        </div>
                        
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                {Array.isArray(exportPreview) && exportPreview.length > 0 ? Object.keys(exportPreview[0] || {}).map((header) => (
                                  <TableHead key={header} className="font-medium">
                                    {header}
                                  </TableHead>
                                )) : null}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {Array.isArray(exportPreview) ? exportPreview.map((row, index) => (
                                <TableRow key={index} className={(row['Man Number'] ?? row['Employee Name'] ?? row['Employee ID']) === 'TOTAL' ? 'bg-gray-100 font-semibold' : ''}>
                                  {Object.entries(row).map(([header, value]: [string, any], cellIndex) => (
                                    <TableCell key={cellIndex} className="text-sm">
                                      {formatPreviewCell(value, header)}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              )) : null}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                    </div>
                    
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button variant="outline" onClick={() => setShowPreview(false)}>
                        Close
                      </Button>
                      <Button variant="outline" onClick={handlePrintPreview}>
                        <Printer className="mr-2 h-4 w-4" />
                        Print
                      </Button>
                      <Button variant="outline" onClick={handleExportPdf} disabled={generatingExport}>
                        {generatingExport ? (
                          <>
                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                            Exporting...
                          </>
                        ) : (
                          <>
                            <FileText className="mr-2 h-4 w-4" />
                            Export PDF
                          </>
                        )}
                      </Button>
                      <Button onClick={handleGenerateExport} disabled={generatingExport}>
                        {generatingExport ? (
                          <>
                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Download className="mr-2 h-4 w-4" />
                            Download CSV
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No data available for preview. Please ensure payroll has been processed for the selected month.
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </TabsContent>}
      </Tabs>
    </div>
  );
};
